import useAddressFields from '@jetshop/core/components/Auth/useAddressFields';
import t, { Intl } from '@jetshop/intl';
import { css, cx } from "linaria";
import React from 'react';
import Input from '../../Forms/Input';
import { CountriesInput } from '@jetshop/ui/Auth/FormFields/CountriesInput';
import { styled } from "linaria/react";
import { Label } from '../../Forms/Input';
import { StyledCarrot } from '@jetshop/ui/Select/Components';
import { theme } from '../../Theme';
import { PID } from './PID';

const sectionStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  ${theme.below.lg} {
    grid-template-columns: 1fr;
  }
`;

const CountryPicker = styled('div')`
 display: none;
`;

const ValidateMobile = (value, code) => {
  let error;
  if (!value) {
    error = t('Required');
  } else if (code === 'SE') {
    if (!/^\d{9,17}$/.test(value)) {
      error = t('Phonenumber must be between 9 and 17 digits');
    } else if (!/^0\d{8,16}$/.test(value)) {
      error = t('Swedish phone numbers must start with a 0');
    } else if (!/^0(?!0)\d{8,16}$$/.test(value)) {
      error = t('Please use only one 0 at the start.');
    }
  } else if (!/^\+\d{2}/i.test(value)) {
    error = t('Phonenumber must start with + and country code');
  } else if (!/^\+[0-9\s-]{9,17}$/i.test(value)) {
    error = t('Invalid format');
  }
  return error;
}

const ValidateNames = (value) => {
  let error;
  if (!value) {
    error = t('Required');
  } else if (!/^.{0,35}$/.test(value)) {
    error = t('Too long');
  } else if (!/^[\p{L}\s\.´`-]+$/u.test(value)) {
    error = t('Value contains invalid characters');
  }
  return error;
}

const ValidateCity = (value) => {
  let error;
  if (!value) {
    error = t('Required');
  } else if (!/^.{0,35}$/.test(value)) {
    error = t('Too long');
  } else if (!/^[\p{L}\s\.´`-]+$/u.test(value)) {
    error = t('Value contains invalid characters');
  }
  return error;
}

const ValidateStreetName = (value) => { 
  let error;
  if (!value) {
    error = t('Required');
  } else if (!/^.{0,35}$/.test(value)) {
    error = t('Too long');
  } else if (!/^[\p{L}\s.,´`\-0-9]+$/u.test(value)) {
    error = t('Invalid format');
  }
  return error;
}

const ValidatePostalCode = (value, code) => {
  let error; 
  if (!value) {
    error = t('Required');
  } 
  if ((code === 'SE' || code === 'FI') && !/^\d{5}$/.test(value)) {
    error = t('Invalid format');
  }
  if ((code === 'NO' || code === 'DK') && !/^\d{4}$/.test(value)) {
    error = t('Invalid format');
  }
  if (code === 'NL' && !/[1-9]\d{3}[A-Z]{2}$/.test(value)) {
    error = t('Invalid format');
  }
  if (code === 'EE' && !/^[A-Z0-9 -]{3,7}$/.test(value)) {
    error = t('Invalid format');
  }
  return error;
}

export default function Address(props) {
  const { fields, setCountryByCode, countries } = useAddressFields();
  function focusSection() {
    // Used to add a highlight to this section when it is active
    if (!props.isActive) props.setActiveSection('address');
  }
  const currentCountryCode = countries[0]?.code;
  return (
    <section
      className={cx('section', props.isActive ? 'active' : null, sectionStyle)}
    >
      {currentCountryCode !== 'FI' && (
        <>
          <PID currentCountryCode={currentCountryCode} />
          <div className="fields-container"></div>
        </>
      )}
      <Intl>
        {(t) =>
          fields.map((field) => {
            if (field.name === 'mobilePhone') {
              return (
                <Input
                {...field.inputProps}
                  required={true}
                  placeholder={t('phonePlaceholder')}
                  validate={(value) => ValidateMobile(value, currentCountryCode)}
                />
              );
            }
            if(field.name === 'firstName' || field.name === 'lastName'){
              return (
                <>
                  <Input
                    onFocus={focusSection}
                    {...field.inputProps}
                    required={true} 
                    validate={ValidateNames}
                  />
                </>
              );
            }
            if(field.name === 'city'){
              return (
                <>
                  <Input
                    onFocus={focusSection}
                    {...field.inputProps}
                    required={true}
                    validate={ValidateNames}
                  />
                </>
              );
            }
            if(field.name === 'streetName'){
              return (
                <>
                  <Input
                    onFocus={focusSection}
                    {...field.inputProps}
                    required={true}
                    validate={ValidateStreetName}
                  />
                </>
              );
            }
            if(field.name === 'postalCode'){
              return (
                <>
                  <Input
                    onFocus={focusSection}
                    {...field.inputProps}
                    required={true}
                    validate={(value) => ValidatePostalCode(value, currentCountryCode)}
                    placeholder={t('Enter number without spaces')}
                  />
                </>
              );
            }
            return <Input onFocus={focusSection} {...field.inputProps} />;
          })
        }
      </Intl>
        <CountryPicker>
          <Label htmlFor="country">{t('Country')}</Label>
          <CountriesInput
            onFocus={focusSection}
            name="country"
            onChange={(e) => setCountryByCode(e.currentTarget.value)}
            countries={countries}
          />
          <StyledCarrot />
        </CountryPicker>
    </section>
  );
}
